import { SagaIterator } from "redux-saga";
import { call, put, select, takeLatest } from "typed-redux-saga";

import { Activity, ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { StoreSagas } from "@com.mgmtp.a12.client/client-core/lib/core/store";
import { Commands } from "@com.mgmtp.a12.formengine/formengine-core/lib/back-end/store";
import {
	FormEngineActions,
	FormEngineSelectors
} from "@com.mgmtp.a12.formengine/formengine-core/lib/client-extensions";
import { UaaSelectors } from "@com.mgmtp.a12.uaa/uaa-authentication-client";

import { getFVManifest } from "../../../app/slices/fachverfahren/selectors";
import { ConfigurationValue, RuntimeFachverfahrenManifest } from "../../../app/slices/fachverfahren/types";
import { PermissionDataholder } from "../../../types/permission";
import { Task, TaskKey } from "../../../types/task";
import { assertNonNullable } from "../../../utilities/assert";
import { isProcessLocked } from "../../../utilities/lockProcessUtils";
import { hasRole } from "../../../utilities/rpcHelper";
import { selectDocument } from "../../../utilities/storeHelpers";
import { WIZARD_TASKS_FVMANIFEST_KEY } from "../../../utilities/variables/modulfConfigurationKeys";
import { ANTRAG_DETAIL_ACTIVITY_ID, TASK_DETAIL_ACTIVITY_ID } from "../../processOverview/store/actions";
import { PERMISSON_DATAHOLDER } from "../../processOverview/store/constants";
import { isDocumentWithProcessMetadata } from "../../workflows/data/processMetadata";
import { selectOperationLockState } from "../../../app/slices/operation/selectors";

import { requestEditableAction } from "./actions";

/**
 * Prüfen, ob das Formular bei der Action "requestEditable" auf readonly gesetzt werden muss und falls ja, setzen.
 */
export function* readonlyStateChangeSaga(): SagaIterator<void> {
	yield* takeLatest(requestEditableAction, function* handle(action) {
		const antragUiState = yield* select(FormEngineSelectors.uiState(ANTRAG_DETAIL_ACTIVITY_ID));
		if (antragUiState && !antragUiState.readonly) {
			return;
		}

		const taskUiState = yield* select(FormEngineSelectors.uiState(TASK_DETAIL_ACTIVITY_ID));
		if (!taskUiState) {
			return;
		}
		const taskScreenLocations = taskUiState.screenLocation[taskUiState.screenLocation.length - 1];

		const document = yield* call(getDocument, TASK_DETAIL_ACTIVITY_ID);
		if (!isDocumentWithProcessMetadata(document)) {
			return;
		}

		const archivalInProgress = document.root.processMetadata.archival?.archivalInProgress;
		const currentTaskScreenName = yield* call(getCurrentTaskScreenName, document);
		const hasUserPermission = yield* call(hasUserPermissionToEdit);
		const user = yield* select(UaaSelectors.user);
		/**
		 * Wenn ein Antrag bearbeitet wird, wird weiter oben abgebrochen.
		 * Falls ein Antrag nicht editiert wird, gibt es drei Möglichkeiten für den Readonly-Modus:
		 * 1. Screen-Name passt nicht zum Task-Name.
		 * 2. Der Prozess ist beendet.
		 * 3. In allen anderen Fällen muss das vier Augen Prinzip betrachtet werden → anhand dessen entscheidet sich, ob readonly notwendig ist oder nicht
		 */
		const isScreenForCurrentTask = taskScreenLocations.locationPath.some(
			e => e.elementName === currentTaskScreenName
		);
		const lockState = yield* select(selectOperationLockState(document.id));
		const processLock = isProcessLocked(lockState, user);
		const isScreenEditable = !isFinished(document) && isScreenForCurrentTask;
		const isEditingPermitted = isScreenEditable && hasUserPermission && !archivalInProgress && !processLock;
		if (!isEditingPermitted) {
			yield* put(
				FormEngineActions.command({
					activityId: TASK_DETAIL_ACTIVITY_ID,
					engineEvent: Commands.setReadonly(true)
				})
			);
		} else {
			const activity = yield* select(ActivitySelectors.activityById(TASK_DETAIL_ACTIVITY_ID));
			assertNonNullable(activity);
			const hasTaskRole =
				action.payload.hasTaskRole ??
				(activity.descriptor.instance ? yield* call(hasRole, activity.descriptor.instance) : false);

			yield* put(
				FormEngineActions.command({
					activityId: TASK_DETAIL_ACTIVITY_ID,
					engineEvent: Commands.setReadonly(!hasTaskRole)
				})
			);
		}
	});
}

function* getDocument(activityId: string): SagaIterator<Activity.Data.Document | undefined> {
	// Wir warten darauf, dass das Document geladen ist.
	const result = yield* call(() => StoreSagas.waitForStateChange(selectDocument(activityId)));

	if (result === undefined) {
		throw new Error("Activity data does not contain a single document.");
	}

	return result;
}

/**
 * Mappt mithilfe der Wizard-Tasks im FV-Manifest einen ScreenName zu einem Task-Key.
 * Wenn kein passender ScreenName gefunden wird, wird der Task-Key zurückgegeben.
 *
 * @param document das aktuelle antragsdokument des Fachverfahrens
 */
function* getCurrentTaskScreenName(document: object): SagaIterator<string | undefined> {
	const fvManifest = yield* call(getFvManifestForCurrentTaskActivity);
	const currentTaskKey = getCurrentTaskKey(document);
	const wizardTasks = fvManifest?.configurationValues.find(
		(c: ConfigurationValue) => c.key === WIZARD_TASKS_FVMANIFEST_KEY
	)?.value;
	if (!wizardTasks) {
		return currentTaskKey;
	}
	const parsedTasks: Task[] = JSON.parse(wizardTasks) || [];
	const matchingScreenName = parsedTasks
		.flatMap((t: Task) => t.key)
		.find((k: TaskKey) => k.taskId === currentTaskKey)?.screenName;
	return matchingScreenName ? matchingScreenName : currentTaskKey;
}

function* hasUserPermissionToEdit(): SagaIterator<boolean> {
	const activity = yield* select(ActivitySelectors.activityById(TASK_DETAIL_ACTIVITY_ID));
	const permissionDataholder = activity?.dataHolders?.find(
		dh => dh?.descriptor?.[PERMISSON_DATAHOLDER] === PERMISSON_DATAHOLDER
	) as Activity.DataHolder<PermissionDataholder> | undefined;

	return permissionDataholder?.data?.isUserPermittedForTask ?? false;
}

function getCurrentTaskKey(document: object | undefined): string | undefined {
	/**
	 * Rufe eine Typeguard auf, um zu prüfen ob document vom Typ "Process_Metadata_D" (Modulf metadata Document)
	 * ist.
	 */
	if (!isDocumentWithProcessMetadata(document)) {
		return undefined;
	}

	//Wenn das Document vom Typ "Process_Metadata_D" können wir Infos aus der processMetadata Gruppe ziehen.
	return document.root.processMetadata.task.key;
}

function isFinished(document: object | undefined): boolean | undefined {
	/**
	 * Rufe eine Typeguard auf, um zu prüfen ob document vom Typ "Process_Metadata_D" (Modulf metadata Document)
	 * ist.
	 */
	if (!isDocumentWithProcessMetadata(document)) {
		return undefined;
	}

	//Wenn das Document vom Typ "Process_Metadata_D" können wir Infos aus der processMetadata Gruppe ziehen.
	return document.root.processMetadata.isFinished;
}

/**
 * Gibt das entsprechende FV-Manifest zu der aktuellen Task-Activity zurück
 */
function* getFvManifestForCurrentTaskActivity(): SagaIterator<RuntimeFachverfahrenManifest | undefined> {
	const taskActivity = yield* select(ActivitySelectors.activityById(TASK_DETAIL_ACTIVITY_ID));
	const defaultDataholder = Activity.findDefaultDataHolder(taskActivity);
	const fvId = defaultDataholder?.descriptor?.fachverfahren ?? "";
	return yield* select(getFVManifest(fvId));
}
